import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type { HubFE } from 'domain/hub';
import { hubsFromRestV1 } from 'domain/mappers/hubFromRestV1';

import { errorHandler, formatAxiosError, isAxiosError } from './errors';
import { correlationHeaders, hubsRestApi } from './restClient';

/**
 * `GET /api/v1/hubs` -> `HubFE[]`. Hubs have no subscription, so unlike `missionV3.ts` /
 * `vehiclesV2.ts` this slice is REST-only and its base query is never invoked.
 *
 * No 204 branch, unlike `getActiveMissionQueryFn`: the spec declares `200 | 401 | 500` for this
 * operation, so there is no body-less success to guard against.
 */
const listHubsQueryFn = async (): Promise<{ data: HubFE[] }> => {
    try {
        const response = await hubsRestApi.listHubs(correlationHeaders());
        return { data: hubsFromRestV1(response.data) };
    } catch (error) {
        const loggableError = isAxiosError(error) ? formatAxiosError(error) : error;
        throw errorHandler('[listHubs]: Unexpected error occurred while fetching the hubs', loggableError);
    }
};

export const hubsApi = createApi({
    reducerPath: 'hubs',
    // The endpoint runs on `queryFn`, so this is never invoked — and keeping a GraphQL base query
    // on a slice that issues no GraphQL request would be misleading.
    baseQuery: fakeBaseQuery<unknown>(),
    refetchOnMountOrArgChange: true,
    endpoints: builder => ({
        // The `string` arg is the cache key, not an input — `` `ListHubs-${userId}` ``, unchanged
        // from the GraphQL endpoint. Giving each user their own entry is what stops one operator's
        // hubs showing while another's refetch is in flight. Do not "clean up" the unread argument.
        listHubs: builder.query<HubFE[], string>({
            queryFn: listHubsQueryFn,
        }),
    }),
});

export const { useListHubsQuery, useLazyListHubsQuery } = hubsApi;
